import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Row, Col, Select, Input, Button, Space } from "antd";
import { SearchOutlined, ReloadOutlined, FilterOutlined } from "@ant-design/icons";
import { agentVariantEnhancedService } from "../../../../api/enhancedApi";

const { Option } = Select;

/**
 * Agent 变体路由规则筛选组件
 *
 * @param {boolean} autoApply - 自动应用模式：表单值变化自动触发 onSearch（防抖 500ms），隐藏搜索/重置按钮
 */
const AgentVariantRuleFilter = ({ onSearch, onReset, collapsed = false, onToggleCollapse, autoApply = false }) => {
    const [form] = Form.useForm();
    const [localAgentNames, setLocalAgentNames] = useState([]);
    const [loading, setLoading] = useState(false);
    const debounceRef = useRef(null);

    // 加载本地Agent名称列表（从已有规则中提取）
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                // 获取本地Agent名称
                const localResponse = await agentVariantEnhancedService.getLocalAgentNames();
                if (localResponse?.data) {
                    setLocalAgentNames(localResponse.data.items || []);
                }
            } catch (error) {
                console.error("加载名称列表失败:", error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    // 处理搜索
    const handleSearch = useCallback(() => {
        form.validateFields().then((values) => {
            const searchParams = { ...values };
            onSearch(searchParams);
        });
    }, [form, onSearch]);

    // 处理重置
    const handleReset = useCallback(() => {
        form.resetFields();
        onReset();
    }, [form, onReset]);

    // autoApply: 表单值变化自动触发搜索（防抖）
    const handleValuesChange = useCallback(() => {
        if (!autoApply) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            handleSearch();
        }, 500);
    }, [autoApply, handleSearch]);

    // 基础筛选条件
    const renderBasicFilters = () => (
        <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="local_agent_name" label="本地Agent名称">
                    <Select
                        placeholder="请选择本地Agent"
                        style={{ width: "100%" }}
                        allowClear
                        showSearch
                        filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        loading={loading}
                    >
                        {localAgentNames.map((name) => (
                            <Option key={name} value={name}>
                                {name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="is_active" label="启用状态">
                    <Select placeholder="请选择状态" style={{ width: "100%" }} allowClear>
                        <Option value={true}>启用</Option>
                        <Option value={false}>停用</Option>
                    </Select>
                </Form.Item>
            </Col>

            {/* autoApply 模式下隐藏手动操作按钮 */}
            {!autoApply && (
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item>
                        <Space>
                            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading}>
                                搜索
                            </Button>
                            <Button icon={<ReloadOutlined />} onClick={handleReset}>
                                重置
                            </Button>
                            {onToggleCollapse && (
                                <Button icon={<FilterOutlined />} onClick={onToggleCollapse} type={collapsed ? "primary" : "default"}>
                                    {collapsed ? "展开" : "收起"}
                                </Button>
                            )}
                        </Space>
                    </Form.Item>
                </Col>
            )}
        </Row>
    );

    // 高级筛选条件
    const renderAdvancedFilters = () => (
        <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="description" label="描述关键词">
                    <Input placeholder="请输入描述关键词" allowClear />
                </Form.Item>
            </Col>
        </Row>
    );

    return (
        <Form
            form={form}
            layout="vertical"
            initialValues={{
                is_active: undefined,
            }}
            onValuesChange={handleValuesChange}
        >
            {renderBasicFilters()}
            {!collapsed && renderAdvancedFilters()}
        </Form>
    );
};

export default AgentVariantRuleFilter;
